/**
 * photos/base64.js — helper de conversion base64 -> Blob para la capa de fotos v2.
 *
 * Reglas:
 *   - tolera base64 SIN prefijo `data:` (default mime image/jpeg SOLO para el cuerpo crudo);
 *   - RESPETA el mime declarado en el dataURL (una firma PNG NO se envía como image/jpeg);
 *   - decodifica via `fetch(dataURL) -> res.blob()`: nativo y ASÍNCRONO, no bloquea el hilo con un
 *     `atob` + loop por byte sobre strings de varios MB (jank/ANR en gama baja, y corre por CADA foto
 *     del batch en el drain);
 *   - NUNCA lanza: ante cualquier fallo devuelve un Blob image/jpeg vacio.
 */

/**
 * Convierte un base64 (dataURL o cuerpo crudo) en un Blob, respetando su mime. Nunca lanza.
 * @param {string} base64  dataURL `data:image/...;base64,xxx` o cuerpo base64 crudo.
 * @returns {Promise<Blob>}
 */
export async function base64ToBlob (base64) {
  try {
    if (!base64 || typeof base64 !== 'string') {
      return new Blob([], { type: 'image/jpeg' })
    }

    let cleaned = base64.trim()

    if (!cleaned.startsWith('data:')) {
      // Cuerpo crudo (sin dataURL): sin mime declarado -> default image/jpeg.
      const comma = cleaned.indexOf(',')
      const body = comma > -1 ? cleaned.substring(comma + 1) : cleaned
      cleaned = 'data:image/jpeg;base64,' + body
    }

    // fetch de un data: URL: decodifica nativo y preserva el mime del propio dataURL en el Blob.
    const res = await fetch(cleaned)
    return await res.blob()
  } catch (e) {
    console.error('[offline-v2] base64ToBlob failed', e)
    return new Blob([], { type: 'image/jpeg' })
  }
}

/**
 * Bytes DECODIFICADOS que produce un dataURL/base64 crudo, SIN decodificarlo (O(len) por el strip de
 * whitespace, sin atob ni fetch). CONTRATO: para toda entrada x, decodedByteLength(x) ===
 * (await base64ToBlob(x)).size — es la base de la verificación de integridad de fotos (photo_meta.
 * byte_length, migración v9): capture() lo sella y los resolvers lo comparan; si difieren, el binario
 * cambió (truncado) y se trata como ausente. Devuelve null si no hay cuerpo decodificable.
 * @param {string} base64  dataURL `data:...;base64,xxx` o cuerpo base64 crudo.
 * @returns {number|null}
 */
export function decodedByteLength (base64) {
  if (!base64 || typeof base64 !== 'string') return null
  let body = base64.trim()
  if (body.startsWith('data:')) {
    const comma = body.indexOf(',')
    if (comma === -1) return null
    body = body.slice(comma + 1)
  } else {
    // mismo manejo que base64ToBlob para cuerpo crudo con una coma perdida
    const comma = body.indexOf(',')
    if (comma > -1) body = body.substring(comma + 1)
  }
  body = body.replace(/\s+/g, '')
  if (!body.length) return null
  const pad = body.endsWith('==') ? 2 : body.endsWith('=') ? 1 : 0
  return Math.floor((body.length * 3) / 4) - pad
}
